<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required, url } from '@vuelidate/validators';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const allowedRedirectOrigins = () => {
  const origins = [window.location.origin];
  const controlPlaneUrl = window.chatwootConfig?.controlPlaneUrl;

  if (controlPlaneUrl) {
    try {
      origins.push(new URL(controlPlaneUrl).origin);
    } catch (error) {
      // Ignore invalid control plane URL config.
    }
  }

  return origins;
};

const sanitizedReturnTo = returnTo => {
  if (!returnTo) {
    return null;
  }

  try {
    const redirectUrl = new URL(returnTo, window.location.origin);
    if (!allowedRedirectOrigins().includes(redirectUrl.origin)) {
      return null;
    }

    return redirectUrl;
  } catch (error) {
    return null;
  }
};

export default {
  components: {
    PageHeader,
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      channelName: '',
      senderKey: '',
      channelPublicId: '',
      apiBaseUrl: '',
      apiKey: '',
      webhookSecret: '',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
  },
  validations: {
    channelName: { required },
    senderKey: { required },
    apiBaseUrl: { required, url },
  },
  methods: {
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      try {
        const kakaoChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          {
            name: this.channelName?.trim(),
            channel: {
              type: 'kakao',
              sender_key: this.senderKey,
              channel_public_id: this.channelPublicId,
              api_base_url: this.apiBaseUrl,
              api_key: this.apiKey,
              webhook_secret: this.webhookSecret,
            },
          }
        );

        const returnTo = sanitizedReturnTo(this.$route.query.return_to);
        if (returnTo) {
          returnTo.searchParams.set('status', 'kakao_connected');
          returnTo.searchParams.set(
            'chatwoot_account_id',
            this.$route.params.accountId
          );
          returnTo.searchParams.set('chatwoot_inbox_id', kakaoChannel.id);
          window.location.assign(returnTo.toString());
          return;
        }

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: kakaoChannel.id,
          },
        });
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.ADD.KAKAO_CHANNEL.API.ERROR_MESSAGE'));
      }
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.KAKAO_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.KAKAO_CHANNEL.DESC')"
    />
    <form
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="createChannel()"
    >
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.channelName.$error }">
          {{ $t('INBOX_MGMT.ADD.KAKAO_CHANNEL.CHANNEL_NAME.LABEL') }}
          <input
            v-model="channelName"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.KAKAO_CHANNEL.CHANNEL_NAME.PLACEHOLDER')
            "
            @blur="v$.channelName.$touch"
          />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.senderKey.$error }">
          {{ $t('INBOX_MGMT.ADD.KAKAO_CHANNEL.SENDER_KEY.LABEL') }}
          <input
            v-model="senderKey"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.KAKAO_CHANNEL.SENDER_KEY.PLACEHOLDER')
            "
            @blur="v$.senderKey.$touch"
          />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label>
          {{ $t('INBOX_MGMT.ADD.KAKAO_CHANNEL.CHANNEL_PUBLIC_ID.LABEL') }}
          <input
            v-model="channelPublicId"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.KAKAO_CHANNEL.CHANNEL_PUBLIC_ID.PLACEHOLDER')
            "
          />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.apiBaseUrl.$error }">
          {{ $t('INBOX_MGMT.ADD.KAKAO_CHANNEL.API_BASE_URL.LABEL') }}
          <input
            v-model="apiBaseUrl"
            type="url"
            :placeholder="
              $t('INBOX_MGMT.ADD.KAKAO_CHANNEL.API_BASE_URL.PLACEHOLDER')
            "
            @blur="v$.apiBaseUrl.$touch"
          />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label>
          {{ $t('INBOX_MGMT.ADD.KAKAO_CHANNEL.API_KEY.LABEL') }}
          <input
            v-model="apiKey"
            type="password"
            :placeholder="
              $t('INBOX_MGMT.ADD.KAKAO_CHANNEL.API_KEY.PLACEHOLDER')
            "
          />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label>
          {{ $t('INBOX_MGMT.ADD.KAKAO_CHANNEL.WEBHOOK_SECRET.LABEL') }}
          <input
            v-model="webhookSecret"
            type="password"
            :placeholder="
              $t('INBOX_MGMT.ADD.KAKAO_CHANNEL.WEBHOOK_SECRET.PLACEHOLDER')
            "
          />
        </label>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="uiFlags.isCreating"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.KAKAO_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>
